import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { AlertTriangle, Download, Trash2, Shield, ChevronRight } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * /account — Store-compliant Delete Account + Data Export screen.
 *
 * Google Play (since 2024) and Apple App Store guideline 5.1.1(v) both
 * require the deletion path be reachable INSIDE the app. GDPR requires the
 * data export too. This one screen satisfies both stores + EU law.
 */
export default function AccountSettings() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  const handleExport = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/me/export");
      const blob = new Blob([JSON.stringify(data, null, 2)],
                            { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `drivons-data-${data.user_id}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t("account.exportReady", "Your data has been downloaded"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (confirmText !== "DELETE") {
      toast.error(t("account.typeDeleteToConfirm", "Type DELETE to confirm"));
      return;
    }
    setBusy(true);
    try {
      await api.delete("/me/account");
      toast.success(t("account.deleted", "Your account has been deleted"));
      logout();
      nav("/", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="max-w-2xl mx-auto px-4 pt-6 pb-24 space-y-6"
      data-testid="account-settings-page"
    >
      {/* Identity card */}
      <header className="rounded-3xl border border-white/10 bg-white/[0.04] p-5">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-white text-black flex items-center justify-center font-black text-base">
            {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="text-lg font-bold truncate">{user?.name || user?.email}</div>
            <div className="text-[13px] text-white/50 truncate">{user?.email}</div>
          </div>
        </div>
      </header>

      {/* Section header */}
      <div className="px-1">
        <h1 className="text-2xl font-black tracking-tight">
          {t("account.title", "Account & Privacy")}
        </h1>
        <p className="text-[13px] text-white/50 mt-1">
          {t("account.pageSub", "Manage your data and privacy under GDPR and store policies.")}
        </p>
      </div>

      {/* Quick links */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] divide-y divide-white/[0.06] overflow-hidden">
        <button
          onClick={() => nav("/account/password")}
          className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-white/[0.04] transition-colors text-start"
          data-testid="account-change-password-link"
        >
          <Shield size={18} className="text-white/70" />
          <div className="flex-1">
            <div className="text-[14px] font-semibold">
              {t("account.changePassword", "Change password")}
            </div>
            <div className="text-[12px] text-white/50">
              {t("account.changePasswordSub", "Keep your account secure")}
            </div>
          </div>
          <ChevronRight size={16} className="text-white/40 rtl-flip" />
        </button>
      </div>

      {/* Export */}
      <section className="rounded-3xl border border-white/10 bg-white/[0.04] p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Download size={18} className="text-white/80" />
          <h2 className="text-[17px] font-bold">{t("account.exportTitle", "Export my data")}</h2>
        </div>
        <p className="text-[13px] text-white/60 leading-relaxed">
          {t("account.exportDesc",
             "Download a JSON file containing your profile, orders, payments, favorites and support history.")}
        </p>
        <button
          data-testid="account-export-btn"
          onClick={handleExport}
          disabled={busy}
          className="pill-btn bg-white text-black rounded-full px-5 py-2.5 text-[13px] font-bold disabled:opacity-50"
        >
          {t("account.exportBtn", "Download my data")}
        </button>
      </section>

      {/* Delete */}
      <section
        className="rounded-3xl border border-brand-warn/30 bg-brand-warn/[0.06] p-5 space-y-3"
        data-testid="account-delete-section"
      >
        <div className="flex items-center gap-2 text-brand-warn">
          <AlertTriangle size={18} />
          <h2 className="text-[17px] font-bold">{t("account.deleteTitle", "Delete my account")}</h2>
        </div>
        <p className="text-[13px] text-white/80 leading-relaxed">
          {t("account.deleteDesc",
             "This permanently removes your profile and personal data. Order history is kept anonymized for financial audit. This action cannot be undone.")}
        </p>
        <div>
          <p className="text-[12px] text-white/60 mb-1.5">
            {t("account.typePrompt", "Type DELETE to confirm:")}
          </p>
          <input
            data-testid="account-delete-confirm-input"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="DELETE"
            className="w-full rounded-2xl bg-black/40 border border-white/10 focus:border-brand-warn focus:outline-none px-4 py-2.5 text-white text-[14px]"
          />
        </div>
        <button
          data-testid="account-delete-btn"
          onClick={handleDelete}
          disabled={busy || confirmText !== "DELETE"}
          className="pill-btn bg-brand-warn text-black rounded-full px-5 py-2.5 text-[13px] font-bold disabled:opacity-40 flex items-center gap-2"
        >
          <Trash2 size={14} /> {t("account.deleteBtn", "Delete my account permanently")}
        </button>
      </section>
    </div>
  );
}
